import { useMemo } from "react";
import { AnnouncementFeedItem, AnnouncementView, MyAnnouncementPost } from "../types/announcement";

const byNewest = (a: { createdAt?: number }, b: { createdAt?: number }) => {
  const aDate = Number(a?.createdAt) || 0;
  const bDate = Number(b?.createdAt) || 0;
  return bDate - aDate;
};

export function useAnnouncementLists({
  myAnnouncements,
  myAnnouncementPosts,
  searchText,
  announcementView,
}: {
  myAnnouncements: AnnouncementFeedItem[];
  myAnnouncementPosts: MyAnnouncementPost[];
  searchText: string;
  announcementView: AnnouncementView;
}) {
  const sortedIncomingAnnouncements = useMemo(() => {
    const arr = Array.isArray(myAnnouncements) ? myAnnouncements.slice() : [];
    const unread = arr.filter((a) => a?.isRead === false).sort(byNewest);
    const read = arr.filter((a) => a?.isRead === true).sort(byNewest);
    return [...unread, ...read];
  }, [myAnnouncements]);

  const filteredAnnouncementFeed = useMemo(() => {
    const q = (searchText || "").trim().toLowerCase();
    if (!q) return sortedIncomingAnnouncements;

    return sortedIncomingAnnouncements.filter((a) => {
      const title = String(a?.title || "").toLowerCase();
      const bodyPreview = String(a?.bodyPreview || "").toLowerCase();
      const authorName = String(a?.authorName || "").toLowerCase();
      const authorCompany = String(a?.authorCompany || "").toLowerCase();
      return title.includes(q) || bodyPreview.includes(q) || authorName.includes(q) || authorCompany.includes(q);
    });
  }, [searchText, sortedIncomingAnnouncements]);

  const sortedMyAnnouncementPosts = useMemo(() => {
    const arr = Array.isArray(myAnnouncementPosts) ? myAnnouncementPosts.slice() : [];
    return arr.sort(byNewest);
  }, [myAnnouncementPosts]);

  const filteredMyAnnouncements = useMemo(() => {
    const q = (searchText || "").trim().toLowerCase();
    if (!q) return sortedMyAnnouncementPosts;

    return sortedMyAnnouncementPosts.filter((a) => {
      const title = String(a?.title || "").toLowerCase();
      const body = String(a?.body || "").toLowerCase();
      return title.includes(q) || body.includes(q);
    });
  }, [searchText, sortedMyAnnouncementPosts]);

  const currentAnnouncementList = announcementView === "feed" ? filteredAnnouncementFeed : filteredMyAnnouncements;

  const unreadAnnouncementsCount = useMemo(() => {
    return (myAnnouncements || []).filter((a) => a?.isRead === false).length;
  }, [myAnnouncements]);

  return {
    filteredAnnouncementFeed,
    filteredMyAnnouncements,
    currentAnnouncementList,
    unreadAnnouncementsCount,
  };
}
